import { useState, useRef } from "react";
import { Box, Text, useThemeUI, Button as ThemeUIButton } from "theme-ui";
import { useSelector } from "react-redux";

import { useTranslation } from "hooks";
import Modal from "../Modal";
import * as styles from "./ModalBladePatternCompare.styles";
import ArrowDownIcon from "components/icons/ArrowDownIcon";
import { VideoPlayIcon, VideoPauseIcon } from "components/icons";
import { DropDown } from "components/Forms";
import selectAllBladePatternOptions from "./selectAllBladePatternOptions";

export default function ModalBladePatternCompare({
  isTopMostModal,
  appLogo,
  title,
  subTitle,
  onClose,
  item,
}) {
  const { t } = useTranslation();
  const {
    theme: { isMobile },
  } = useThemeUI();

  const allBladePatternOptions = useSelector(selectAllBladePatternOptions);

  const [patternIndexes, setPatternIndexes] = useState([
    allBladePatternOptions
      .findIndex((option) => item === option.title)
      .toString(),
    null,
    null,
  ]);

  const allPatterns = allBladePatternOptions.map((option, index) => {
    return { value: index.toString(), label: option.title };
  });

  const detailsFields = allBladePatternOptions[0].details;

  const onPatternSelectChange = (index) => (value) => {
    const newPatternIndexes = [...patternIndexes];
    newPatternIndexes[index] = value;
    setPatternIndexes(newPatternIndexes);
  };

  // Video playing
  const [videoIsPlaying, setVideoIsPlaying] = useState(false);

  const container = useRef();
  const videoRefs = useRef([]);

  const onPlayClick = (index) => () => {
    const videoEl = videoRefs.current[index];
    setVideoIsPlaying(true);

    videoRefs.current.forEach((video) => {
      video.currentTime = videoEl.currentTime;
      video.play();
    });
  };

  const onPauseClick = (index) => () => {
    const videoEl = videoRefs.current[index];
    setVideoIsPlaying(false);

    videoRefs.current.forEach((video) => {
      video.currentTime = videoEl.currentTime;
      video.pause();
    });
  };

  const onVideoEnded = () => {
    setVideoIsPlaying(false);
  };

  return (
    <Modal
      appLogo={appLogo}
      outerTitle={t("Blade Pattern - Compare Patterns")}
      title={title}
      subTitle={subTitle}
      onClose={onClose}
      closeButtonLabel={t("CLOSE BLADE PATTERN COMPARE")}
      isFullWidth={true}
      isTopMostModal={isTopMostModal}
    >
      <Box sx={styles.contentContainer} ref={container}>
        {patternIndexes
          .slice(0, isMobile ? 2 : 3)
          .map((patternIndex, index) => {
            const pattern =
              allBladePatternOptions[parseInt(patternIndex, 10)] || {};
            return (
              <Box key={index} sx={styles.contentItemContainer}>
                <Box sx={styles.videoContainer}>
                  {pattern?.video ? (
                    <Box sx={styles.videoControlsContainer}>
                      <video
                        onEnded={onVideoEnded}
                        ref={(vid) => (videoRefs.current[index] = vid)}
                        src={pattern.video.url}
                        style={{ width: "100%", display: "table-row" }}
                      ></video>
                      {videoIsPlaying ? (
                        <ThemeUIButton
                          onClick={onPauseClick(index)}
                          variant="unstyled"
                          sx={styles.videoPauseButton}
                          id="modal-compare-video-play-button"
                        >
                          <VideoPauseIcon />
                        </ThemeUIButton>
                      ) : (
                        <ThemeUIButton
                          onClick={onPlayClick(index)}
                          variant="unstyled"
                          sx={styles.videoPlayButton}
                        >
                          <VideoPlayIcon />
                        </ThemeUIButton>
                      )}
                    </Box>
                  ) : (
                    <Box sx={styles.emptyVideoBox}>
                      <Text sx={styles.emptyVideoBoxContent}>
                        Select a blade pattern to compare
                      </Text>
                      <Box sx={styles.downArrow}>
                        <ArrowDownIcon></ArrowDownIcon>
                      </Box>
                    </Box>
                  )}
                </Box>
                <Box sx={styles.dropdownContainer}>
                  <Text sx={styles.dropdownLabel}>Blade Pattern</Text>
                  <DropDown
                    options={allPatterns.filter(
                      ({ value }) =>
                        !patternIndexes
                          .filter((_a, pIndex) => pIndex !== index)
                          .includes(value)
                    )}
                    placeholder={t("Select Blade Pattern")}
                    value={patternIndex !== null ? patternIndex : ""}
                    onChange={onPatternSelectChange(index)}
                    size="medium"
                    width="100%"
                  ></DropDown>
                </Box>
                <Box sx={styles.compareInfoContainer}>
                  {detailsFields.map((field) => (
                    <Text key={field.label} sx={styles.modalContentRow}>
                      {field.label}:
                      <Text sx={styles.modalContentText}>
                        {" "}
                        {pattern.title ? field.value : "--"}
                      </Text>
                    </Text>
                  ))}
                </Box>
              </Box>
            );
          })}
      </Box>
    </Modal>
  );
}
